import {
	DocumentStoreInitializationState,
	IDocumentStoreInitializationModel
} from "client/Api/Documents/documents";
import {
	areAllStoresNotConfigured,
	didAllConfiguredStoresFailed,
	getConfiguredStoreTypes,
	useInitializer
} from "client/Features/Documents/useInitializer";
import { DocumentStoreType } from "client/Api/Documents/common";
import { documentsConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import {
	Fragment,
	ReactElement,
	useEffect,
	useMemo
} from "react";
import { BookmarksSection } from "./BookmarksSection";
import { never } from "client_legacy/ndaw/errors";
import { SharePointSection } from "./SharePointSection";
import {
	ensureNotNil,
	isNil
} from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import { WebDavSection } from "./WebDavSection";
import { NoResults } from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import { useForceRerender } from "client/Hooks";

export type DocumentStructureProps = {
	onIsLoadingChange(isLoading: boolean): void;
};

export const DocumentStructure = (props: DocumentStructureProps): ReactElement => {
	const {
		onIsLoadingChange
	} = props;

	const [reload, initialStoreInitializationModelsVersion] = useForceRerender();

	const initialStoreInitializationModels = useMemo(
		(): readonly IDocumentStoreInitializationModel[] => {
			hasImplicitDependencyOn(initialStoreInitializationModelsVersion);

			const {
				sharePoint,
				webDav
			} = documentsConfiguration();

			return [
				{
					storeType: DocumentStoreType.SharePoint,
					state: sharePoint.isConfigured
						? DocumentStoreInitializationState.InProgress
						: DocumentStoreInitializationState.NotConfigured
				},
				{
					storeType: DocumentStoreType.WebDav,
					state: !webDav
						? DocumentStoreInitializationState.NoLicense
						: webDav.isConfigured
							? DocumentStoreInitializationState.InProgress
							: DocumentStoreInitializationState.NotConfigured
				}
			];
		},
		[initialStoreInitializationModelsVersion]
	);

	const storeInitializationModels: readonly IDocumentStoreInitializationModel[] | undefined = useInitializer(
		initialStoreInitializationModels,
		true
	);

	const storeTypesToDisplay = useMemo(
		(): readonly DocumentStoreType[] => {
			if (!storeInitializationModels) {
				return [];
			}

			const availableStores: readonly IDocumentStoreInitializationModel[] = storeInitializationModels.filter(
				x => x.state !== DocumentStoreInitializationState.NoLicense
			);

			let typesToDisplay: readonly DocumentStoreType[] = getConfiguredStoreTypes(availableStores);

			if (storeInitializationModels.some(x => x.state === DocumentStoreInitializationState.Success)) {
				typesToDisplay = [DocumentStoreType.Bookmarks, ...typesToDisplay];
			}

			return typesToDisplay;
		},
		[storeInitializationModels]
	);

	useEffect(
		(): void => {
			onIsLoadingChange(isNil(storeInitializationModels));
		},
		[
			onIsLoadingChange,
			storeInitializationModels
		]
	);

	const renderSectionForStoreType = (storeType: DocumentStoreType): ReactElement => {
		switch (storeType) {
			case DocumentStoreType.Bookmarks:
				return <BookmarksSection />;
			case DocumentStoreType.SharePoint:
				const sharePointInitializationModel: IDocumentStoreInitializationModel = ensureNotNil(
					storeInitializationModels?.find(x => x.storeType === DocumentStoreType.SharePoint)
				);
				return (
					<SharePointSection
						hasInitializationError={sharePointInitializationModel.state !== DocumentStoreInitializationState.Success}
					/>
				);
			case DocumentStoreType.WebDav:
				const webDavInitializationModel: IDocumentStoreInitializationModel = ensureNotNil(
					storeInitializationModels?.find(x => x.storeType === DocumentStoreType.WebDav)
				);
				return (
					<WebDavSection
						hasInitializationError={webDavInitializationModel.state !== DocumentStoreInitializationState.Success}
					/>
				);
			default:
				never(storeType);
		}
	};

	const hasAllStoresAreNotConfiguredError = useMemo(
		(): boolean => {
			if (!storeInitializationModels) {
				return false;
			}

			return areAllStoresNotConfigured(storeInitializationModels);
		},
		[storeInitializationModels]
	);

	const hasAllConfiguredStoresFailed = useMemo(
		(): boolean => {
			if (!storeInitializationModels) {
				return false;
			}

			return didAllConfiguredStoresFailed(storeInitializationModels);
		},
		[storeInitializationModels]
	);

	if (hasAllStoresAreNotConfiguredError) {
		return (
			<NoResults
				title={localize("_DocumentsIsNotConfiguredTitle")}
				icon="gear"
				message={localize("_DocumentsIsNotConfiguredUserBody")}
				height="fill"
			/>
		);
	}

	if (hasAllConfiguredStoresFailed) {
		return (
			<NoResults
				title={localize("_UnableToRetrieveItems")}
				icon="gear"
				message={localize("_WeCantRetrieveAnyItemsAtThisMomentPleaseRetry")}
				onRetry={reload}
				height="fill"
			/>
		);
	}

	return (
		<>
			{storeTypesToDisplay.map(
				s => (
					<Fragment key={s}>
						{renderSectionForStoreType(s)}
					</Fragment>
				)
			)}
		</>
	);
};
DocumentStructure.displayName = "DocumentStructure";